// The frames of the Restaurant OS: the dark top bar + bottom tabs of the floor
// (Main.dc.html), the light left rail of the order pad (OrderPad.dc.html), the
// signed-in person's menu (screens they work on, sign out).
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { C } from '../theme/tokens';
import { initials, shortName } from '../lib/format';
import { ROLE_LABEL, screensFor, signOutStaff, signInHref } from '../lib/session';
import { Avatar, LiveIndicator } from './ui';
import { useChatUnread } from '../lib/chat';
import { IconCard, IconChat, IconFlame, IconLogout, IconOrder, IconPass, IconTables } from './icons';

const SCREEN_ICON = { floor: IconTables, kds: IconFlame, expo: IconPass, checkout: IconCard, order: IconOrder, chat: IconChat };

export function UserMenu({ me, dark = true, size = 40 }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const screens = screensFor(me);
  const signOut = () => {
    signOutStaff();
    window.location.assign(signInHref('/dining'));
  };
  return (
    <div ref={wrap} style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
        aria-label={`Signed in as ${me?.name || 'staff'}`} style={{ border: 'none', background: 'transparent', padding: 0, borderRadius: '50%' }}>
        <Avatar text={initials(me?.name)} ring={dark ? C.blueHi : undefined} size={size} dark={dark} />
      </button>
      {open && (
        <div role="menu" className="c29-in" style={{ position: 'absolute', right: 0, top: size + 8, zIndex: 75, width: 280, borderRadius: 18, background: '#FFFFFF', color: C.ink, border: `1px solid ${C.border}`, boxShadow: '0 24px 60px rgba(0,0,0,.35)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.hair}` }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{me?.name || 'Signed in'}</div>
            <div style={{ fontSize: 12.5, color: C.muted }}>{[ROLE_LABEL[me?.role] || me?.role, me?.restaurant_name].filter(Boolean).join(' · ')}</div>
          </div>
          {screens.map((s) => {
            const Icon = SCREEN_ICON[s.key];
            return (
              <button key={s.key} type="button" role="menuitem" onClick={() => { setOpen(false); navigate(`/dining/${s.path}`); }}
                style={{ width: '100%', height: 48, border: 'none', background: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', fontSize: 14.5, fontWeight: 600, color: C.ink }}>
                <Icon size={20} />{s.label}
              </button>
            );
          })}
          <button type="button" role="menuitem" onClick={signOut}
            style={{ width: '100%', height: 50, border: 'none', borderTop: `1px solid ${C.hair}`, background: C.cream, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', fontSize: 14.5, fontWeight: 700, color: C.redInk }}>
            <IconLogout size={20} />Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function DarkTopBar({ title, sub, height = 68, children, right, logo = 36 }) {
  return (
    <header style={{ height, flex: 'none', display: 'flex', alignItems: 'center', gap: 20, padding: '0 20px', background: C.bar, borderBottom: `1px solid ${C.line}`, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 'none' }}>
        <div aria-hidden="true" className="c29-tight" style={{ width: logo, height: logo, borderRadius: logo > 40 ? 12 : 10, background: C.orange, color: C.orangeDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: Math.round(logo * 0.44), letterSpacing: '-0.5px' }}>29</div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
          <span style={{ fontSize: logo > 40 ? 20 : 17, fontWeight: logo > 40 ? 800 : 700, color: C.textHi, whiteSpace: 'nowrap' }}>{title}</span>
          {sub && <span style={{ fontSize: logo > 40 ? 13 : 12, color: C.textLo, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>}
        </div>
      </div>
      {children}
      <div style={{ flex: 1 }} />
      {right}
    </header>
  );
}

export function BottomNav({ me, active, badges: given = {} }) {
  const screens = screensFor(me);
  const chatUnread = useChatUnread(me); // guest chats waiting for an answer
  const badges = { chat: chatUnread, ...given };
  if (screens.length < 2) return null;
  return (
    <nav aria-label="Main" style={{ height: 72, flex: 'none', display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: 8, background: C.bar, borderTop: `1px solid ${C.line}` }}>
      {screens.map((s) => {
        const Icon = SCREEN_ICON[s.key];
        const on = s.key === active;
        return (
          <NavLink key={s.key} to={`/dining/${s.path}`} aria-current={on ? 'page' : undefined}
            style={{ width: 132, maxWidth: '25vw', textDecoration: 'none', color: on ? C.orange : C.textLo, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 12.5, fontWeight: on ? 700 : 600, position: 'relative' }}>
            <Icon size={24} />{s.label}
            {badges[s.key] > 0 && (
              <span style={{ position: 'absolute', top: 10, left: 'calc(50% + 10px)', minWidth: 18, height: 18, borderRadius: 9, background: C.amber, color: '#1F1300', fontSize: 10.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{badges[s.key]}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function LeftRail({ me, active, streamStatus, extra }) {
  const screens = screensFor(me);
  const items = [];
  screens.forEach((s) => {
    items.push(s);
    if (s.key === 'floor' && active === 'order') items.push({ key: 'order', label: 'Order', path: null });
  });
  return (
    <nav aria-label="Main" style={{ width: 84, flex: 'none', background: '#FFFFFF', borderRight: '1px solid #ECE6DF', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0', gap: 8 }}>
      <div aria-hidden="true" className="c29-tight" style={{ width: 44, height: 44, borderRadius: 12, background: C.orange, color: C.orangeDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, marginBottom: 16 }}>29</div>
      {items.map((s) => {
        const Icon = SCREEN_ICON[s.key];
        const on = s.key === active;
        const style = { width: 64, height: 60, borderRadius: 14, border: 'none', background: on ? C.orangeSoft : 'transparent', color: on ? C.orangeInk : C.muted, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 11, fontWeight: on ? 700 : 600, textDecoration: 'none' };
        if (!s.path) return <span key={s.key} aria-current="page" style={style}><Icon size={22} />{s.label}</span>;
        return <NavLink key={s.key} to={`/dining/${s.path}`} aria-current={on ? 'page' : undefined} style={style}><Icon size={22} />{s.label}</NavLink>;
      })}
      <div style={{ flex: 1 }} />
      {extra}
      <div style={{ transform: 'scale(.85)' }}><LiveIndicator status={streamStatus} light size={11} /></div>
      <UserMenu me={me} dark={false} size={44} />
      <span style={{ fontSize: 10.5, color: C.faint, fontWeight: 600, maxWidth: 76, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortName(me?.name)}</span>
    </nav>
  );
}
